import React, { useState } from 'react';
import { loadOrCreateIdentity } from '../lib/identity';
import { seedFile } from '../lib/torrent';
import { saveVideo, updateVideoThumbnail, VideoMetadata } from '../lib/metadata';
import { saveFile, getStorageInfo } from '../lib/fileStorage';
import { publishEvent } from '../lib/sync';
import { COLORS, SPACING } from '../styles/theme';

async function captureVideoThumbnail(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    const cleanup = () => URL.revokeObjectURL(url);
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(1, video.duration * 0.1);
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 240;
        canvas.height = 135;
        const ctx = canvas.getContext('2d');
        if (!ctx) { cleanup(); resolve(null); return; }
        ctx.drawImage(video, 0, 0, 240, 135);
        let quality = 0.7;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        while (dataUrl.length > 70000 && quality > 0.1) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }
        cleanup();
        resolve(dataUrl.length <= 70000 ? dataUrl : null);
      } catch { cleanup(); resolve(null); }
    };
    video.onerror = () => { cleanup(); resolve(null); };
    video.load();
  });
}

export function Upload({ defaultOpen = false }: { defaultOpen?: boolean } = {}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [storageInfo, setStorageInfo] = useState<any>(null);
  const [showForm, setShowForm] = useState(defaultOpen);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    updateStorageInfo();
  }, []);

  const updateStorageInfo = async () => {
    const info = await getStorageInfo();
    setStorageInfo(info);
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.size);

    if (file.size > 500 * 1024 * 1024) {
      setMessage('File exceeds 500MB limit');
      return;
    }

    setLoading(true);
    setMessage('Uploading...');

    try {
      console.log('Starting upload:', file.name);
      const identity = loadOrCreateIdentity();
      console.log('Identity loaded:', identity.userId.slice(0, 8));

      const torrentInfo = await seedFile(file);
      console.log('Torrent created:', torrentInfo.infoHash);

      await saveFile(torrentInfo.infoHash, file, file.name);
      console.log('File saved to storage');

      const metadata: VideoMetadata = {
        hash: torrentInfo.infoHash,
        title: title || file.name,
        description,
        uploader: identity.userId,
        uploadedAt: Date.now(),
        magnetURI: torrentInfo.magnetURI,
        fileSize: file.size,
        tags: tags.split(',').map(t => t.trim()).filter(t => t)
      };

      saveVideo(metadata);

      captureVideoThumbnail(file).then(thumb => {
        if (thumb) updateVideoThumbnail(torrentInfo.infoHash, thumb);
      });

      await publishEvent('video', metadata);
      updateStorageInfo();

      setMessage(`Complete — ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      setTitle('');
      setDescription('');
      setTags('');
      setShowForm(false);
    } catch (error) {
      console.error('Upload error:', error);
      setMessage('Upload failed: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const storagePercent = storageInfo 
    ? Math.round((storageInfo.usage / storageInfo.quota) * 100) 
    : 0;

  return (
    <div style={{
      border: `2px outset ${COLORS.highlight}`,
      marginBottom: SPACING.lg,
      backgroundColor: COLORS.panel,
      overflow: 'hidden'
    }}>
      <div style={{
        backgroundColor: COLORS.accent,
        color: '#fff',
        padding: `${SPACING.xs} ${SPACING.sm}`,
        borderBottom: `2px solid ${COLORS.highlight}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '10px',
        fontWeight: 700,
        userSelect: 'none',
        cursor: 'default'
      }}>
        <span>Upload</span>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            backgroundColor: COLORS.panel,
            color: COLORS.accent,
            border: `2px outset ${COLORS.highlight}`,
            padding: `${SPACING.xs} ${SPACING.sm}`,
            cursor: 'pointer',
            fontFamily: 'monospace',
            fontSize: '9px',
            fontWeight: 700,
            textDecoration: 'underline',
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.5)',
            minHeight: '32px'
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.boxShadow = 'inset 1px 1px 2px rgba(0,0,0,0.8)';
            e.currentTarget.style.borderStyle = 'inset';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.boxShadow = 'inset 0 0 0 1px rgba(0,0,0,0.5)';
            e.currentTarget.style.borderStyle = 'outset';
          }}
        >
          {showForm ? '✕' : '+'} Video
        </button>
      </div>

      {storageInfo && (
        <div style={{
          padding: SPACING.md,
          backgroundColor: COLORS.bg,
          borderBottom: `1px solid ${COLORS.border}`,
          fontSize: '9px',
          color: COLORS.textMuted
        }}>
          <div style={{ marginBottom: SPACING.sm }}>
            Storage: {(storageInfo.usage / 1024 / 1024).toFixed(1)}MB / {(storageInfo.quota / 1024 / 1024).toFixed(0)}MB
          </div>
          <div style={{
            width: '100%',
            height: '12px',
            backgroundColor: '#000',
            border: `1px solid ${COLORS.border}`,
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${storagePercent}%`,
              height: '100%',
              backgroundColor: COLORS.accent
            }} />
          </div>
        </div>
      )}

      {showForm && (
        <div style={{
          padding: SPACING.lg,
          backgroundColor: COLORS.bg,
          borderTop: `2px inset ${COLORS.highlight}`
        }}>
          <div style={{ marginBottom: SPACING.lg }}>
            <label style={{
              display: 'block',
              fontSize: '9px',
              marginBottom: SPACING.sm,
              color: COLORS.textMuted,
              fontWeight: 700
            }}>
              Title
            </label>
            <input
              type="text"
              placeholder="Video title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              style={{
                width: '100%',
                padding: SPACING.md,
                backgroundColor: '#fff',
                color: '#000',
                border: `2px inset ${COLORS.highlight}`,
                fontFamily: 'monospace',
                fontSize: '11px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{ marginBottom: SPACING.lg }}>
            <label style={{
              display: 'block',
              fontSize: '9px',
              marginBottom: SPACING.sm,
              color: COLORS.textMuted,
              fontWeight: 700
            }}>
              Description
            </label>
            <textarea
              placeholder="Optional description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              style={{
                width: '100%',
                padding: SPACING.md,
                minHeight: '80px',
                backgroundColor: '#fff',
                color: '#000',
                border: `2px inset ${COLORS.highlight}`,
                fontFamily: 'monospace',
                fontSize: '11px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{ marginBottom: SPACING.lg }}>
            <label style={{
              display: 'block',
              fontSize: '9px',
              marginBottom: SPACING.sm,
              color: COLORS.textMuted,
              fontWeight: 700
            }}>
              Tags
            </label>
            <input
              type="text"
              placeholder="comma, separated, tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              style={{
                width: '100%',
                padding: SPACING.md,
                backgroundColor: '#fff',
                color: '#000',
                border: `2px inset ${COLORS.highlight}`,
                fontFamily: 'monospace',
                fontSize: '11px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{ marginBottom: SPACING.lg }}>
            <button
              onClick={handleFileClick}
              disabled={loading}
              style={{
                display: 'block',
                width: '100%',
                padding: SPACING.md,
                backgroundColor: '#fff',
                color: '#000',
                border: `2px inset ${COLORS.highlight}`,
                cursor: loading ? 'default' : 'pointer',
                fontSize: '9px',
                fontWeight: 700,
                fontFamily: 'monospace',
                textDecoration: 'underline',
                userSelect: 'none'
              }}
            >
              {loading ? 'Uploading...' : 'Select video file'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleUpload}
              style={{ display: 'none' }}
            />
          </div>

          {message && (
            <div style={{
              padding: SPACING.md,
              backgroundColor: message.includes('Complete') ? '#001F00' : message.includes('failed') ? 'rgba(139, 30, 45, 0.2)' : COLORS.bg,
              border: `1px solid ${message.includes('Complete') ? '#00AA00' : message.includes('failed') ? COLORS.accent : COLORS.border}`,
              fontSize: '9px',
              color: message.includes('Complete') ? '#00AA00' : message.includes('failed') ? COLORS.accent : COLORS.textMuted,
              fontWeight: 700
            }}>
              {message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
